import {
  Error,
  MultiSkeleton,
  PageContainer,
  PageTitle,
  PrimaryButton,
  TeachingMaterialsPowerPointListItem,
} from '@/components'
import { useCallback, useState } from 'react'

import { ArrowLeft } from 'lucide-react'
import { PDFListItem } from '@/components/teachingMaterials/PDFListItem/PDFListItem'
import { TeachingMaterialsFullScreenPowerPointView } from '@/components/teachingMaterials/TeachingMaterialsFullScreenPowerPointView/TeachingMaterialsFullScreenPowerPointView'
import { createFileRoute } from '@tanstack/react-router'
import { getTeacherGuidanceListQueryOptions } from '@/requestObjects'
import { useAuthHeaders } from '@/providers'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute(
  '/_authRoutes/_appWrapper/teacher-guidelines',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const headers = useAuthHeaders()
  const {
    data: teacherGuidanceList,
    isLoading,
    isError,
  } = useQuery({
    ...getTeacherGuidanceListQueryOptions({ headers }),
  })
  const [startingSlide, setStartingSlide] = useState<number | undefined>()
  const [fullScreenIndex, setFullScreenIndex] = useState<number | undefined>()
  const handleClick = useCallback(
    ({
      currentSlide,
      guidanceIndex,
    }: {
      currentSlide?: number
      guidanceIndex: number
    }) => {
      setStartingSlide(currentSlide)
      setFullScreenIndex(guidanceIndex)
    },
    [setFullScreenIndex],
  )

  const handleClosePresentation = useCallback(() => {
    setFullScreenIndex(undefined)
    setStartingSlide(undefined)
  }, [])
  return (
    <PageContainer>
      <div className="space-y-6">
        <PageTitle
          title={t('navigation.teacherGuidelines')}
          subtitle={t('navigation.teacherGuidelinesSubtitle')}
        />
        {isError && <Error />}
        <>
          {typeof fullScreenIndex !== 'number' ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-4">
              {isLoading ? (
                <MultiSkeleton
                  count={8}
                  className="aspect-[10/10.6] lg:aspect-[10/8.6]"
                />
              ) : (
                <>
                  {teacherGuidanceList?.map(
                    ({ name, description, s3_uris = [], file_type }, index) => {
                      if (file_type === 'pdf') {
                        return (
                          <PDFListItem
                            name={name}
                            onClick={() => {
                              handleClick({ guidanceIndex: index })
                            }}
                          />
                        )
                      }
                      return (
                        <TeachingMaterialsPowerPointListItem
                          key={name}
                          name={name}
                          description={description}
                          s3_uris={s3_uris}
                          onClick={(slide) =>
                            handleClick({
                              currentSlide: slide,
                              guidanceIndex: index,
                            })
                          }
                        />
                      )
                    },
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {typeof startingSlide === 'number' ? (
                <TeachingMaterialsFullScreenPowerPointView
                  slideImages={
                    teacherGuidanceList?.[fullScreenIndex]?.s3_uris || []
                  }
                  startingSlide={startingSlide}
                  onClosePresentation={handleClosePresentation}
                />
              ) : (
                <>
                  <PrimaryButton
                    variant="outline"
                    onClick={handleClosePresentation}
                  >
                    <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
                    {t('common.back')}
                  </PrimaryButton>
                  <div className="w-full h-[calc(100dvh-76px)] border rounded-lg overflow-hidden">
                    <iframe
                      src={teacherGuidanceList?.[fullScreenIndex]?.s3_uris?.[0]}
                      className="w-full h-full"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </>
      </div>
    </PageContainer>
  )
}
